import { Component, inject, input, InputSignal, WritableSignal } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TitleCasePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconComponent } from '@kinomap-dev/ng-ui-lib/basics';
import { CreateContentService } from '../../services/create-content.service';
import { ColorService } from '../../services/color.service';
import { ModalService } from '../../services/modal-service';
import { Toaster } from '../../services/toaster';
import { convertZwoToWorkout } from '../../utils/convert-zwo-to-sections.helpers';
import {
  ZwoImportError,
  ZwoImportErrorCode,
  ZwoImportResult,
  ZwoImportWarning,
  ZwoImportWarningCode,
} from '../../types/zwo.type';

@Component({
  selector: 'app-workout-uploader',
  imports: [TranslateModule, IconComponent, TitleCasePipe],
  templateUrl: './workout-uploader.html',
  styleUrl: './workout-uploader.scss',
})
export class WorkoutUploader {
  readonly colorService: ColorService = inject(ColorService);
  private readonly createContentService: CreateContentService = inject(CreateContentService);
  private readonly modalService: ModalService = inject(ModalService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly toaster: Toaster = inject(Toaster);

  readonly workoutForm: InputSignal<FormGroup> = input.required<FormGroup>();

  protected readonly importedFile: WritableSignal<File | undefined> =
    this.createContentService.workoutImportedFile;

  onSelectFile(event: Event): void {
    const fileInput: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | undefined = fileInput.files?.[0];
    fileInput.value = '';
    if (file) this.importFile(file);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    const file: File | undefined = event.dataTransfer?.files[0];
    if (file) this.importFile(file);
  }

  onRemoveFile(): void {
    this.workoutForm()
      .get('trainingBlock')
      ?.patchValue({ sport: undefined, workoutMode: undefined });
    this.createContentService.workoutSections.set([]);
    this.createContentService.workoutSelectedSectionsIds.set([]);
    this.importedFile.set(undefined);
  }

  private async importFile(file: File): Promise<void> {
    let result: ZwoImportResult;
    try {
      result = convertZwoToWorkout(await file.text());
    } catch (error) {
      this.showError(error instanceof ZwoImportError ? error.code : 'invalid_file');
      return;
    }

    if (!this.createContentService.workoutSections().length) {
      this.applyImport(result, file);
      return;
    }

    this.modalService
      .openConfirmation<'confirm' | 'cancel'>({
        title: 'confirm_reset_sections.title',
        message: 'confirm_reset_sections.message',
        buttons: [
          { action: 'cancel', label: 'common.cancel', style: 'secondary' },
          { action: 'confirm', label: 'common.validate', style: 'primary' },
        ],
      })
      .closed.subscribe((action: 'confirm' | 'cancel' | undefined) => {
        if (action === 'confirm') this.applyImport(result, file);
      });
  }

  private applyImport(result: ZwoImportResult, file: File): void {
    this.workoutForm()
      .get('trainingBlock')
      ?.patchValue({ sport: result.sport, workoutMode: result.mode });
    this.createContentService.workoutSections.set(result.sections);
    this.createContentService.workoutSelectedSectionsIds.set([]);

    const information: Record<string, string | undefined> = {
      title: result.name,
      description: result.description,
    };

    Object.entries(information).forEach(([field, value]: [string, string | undefined]) => {
      const control: AbstractControl | null = this.workoutForm().get(`informationBlock.${field}`);
      if (value && control && !control.value) control.setValue(value);
    });

    this.importedFile.set(file);
    this.showFeedback(result);
  }

  private showFeedback(result: ZwoImportResult): void {
    const codes: ZwoImportWarningCode[] = [
      ...new Set(result.warnings.map((warning: ZwoImportWarning) => warning.code)),
    ];
    const success: string = this.translateService.instant('workout_uploader.success', {
      count: result.sections.length,
    });

    if (!codes.length) {
      this.toaster.success(
        this.translateService.instant('workout_uploader.success_title'),
        success,
        'upload',
      );
      return;
    }

    const adjustments: string = codes
      .map((code: ZwoImportWarningCode) =>
        this.translateService.instant(`workout_uploader.warnings.${code}`),
      )
      .join(', ');
    this.toaster.warning(
      this.translateService.instant('workout_uploader.warnings_title'),
      `${success} ${adjustments}.`,
      'warning',
      6000,
    );
  }

  private showError(code: ZwoImportErrorCode): void {
    this.toaster.error(
      this.translateService.instant('workout_uploader.error_title'),
      this.translateService.instant(`workout_uploader.errors.${code}`),
      'warning',
      6000,
    );
  }
}
